import React, { useState, useEffect } from "react";
import { Box, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { Text } from "@src/lib/ui-react";
import {
  HOLIDAY_DIFFERENTIAL,
  HOLIDAY_INPUT_PROPS,
  truncateToTwoDecimals,
} from "../../constants/facility";
import { HolidayInput } from "./holidayInput";

interface HolidayRateConfig {
  type: string;
  increment: number;
}

interface Props {
  holidayRateConfig?: HolidayRateConfig;
  handleChange: (option: string, value: number) => void;
}

export function HolidayRate(props: Props) {
  const { holidayRateConfig, handleChange } = props;
  const [chargeValue, setChargeValue] = useState<string | number | undefined>(1.5);
  const [flatIncreaseValue, setFlatIncreaseValue] = useState(20);
  const [option, setOption] = useState<string>("");
  const { FLAT, CHARGE_RATE_MULTIPLIER } = HOLIDAY_DIFFERENTIAL;
  const { MULTIPLIER_PROPS, FLAT_PROPS } = HOLIDAY_INPUT_PROPS;

  useEffect(() => {
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (holidayRateConfig && holidayRateConfig.type === CHARGE_RATE_MULTIPLIER) {
      setOption(CHARGE_RATE_MULTIPLIER);
      setChargeValue(holidayRateConfig.increment);
    }
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (holidayRateConfig && holidayRateConfig.type === FLAT) {
      setOption(FLAT);
      setFlatIncreaseValue(holidayRateConfig.increment);
    }
  }, [holidayRateConfig, CHARGE_RATE_MULTIPLIER, FLAT]);

  function onOptionChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedOption = event.target.value;
    setOption(selectedOption);
    if (selectedOption === FLAT) {
      handleChange(selectedOption, flatIncreaseValue);
    } else {
      handleChange(selectedOption, Number(chargeValue));
    }
  }

  function handleHolidayRateOnChange(value: number) {
    if (option === CHARGE_RATE_MULTIPLIER) {
      const truncatedValue = truncateToTwoDecimals(value);
      setChargeValue(truncatedValue);
      handleChange(option, truncatedValue);
    } else {
      setFlatIncreaseValue(value);
      handleChange(option, value);
    }
  }

  return (
    <Box>
      <Text sx={(theme) => ({ color: theme.palette.text.secondary, marginBottom: 2 })}>
        On Holidays do we charge a flat increase or a multiple of the charge rate?
      </Text>

      <RadioGroup onChange={onOptionChangeHandler} value={option} id="holidayRateConfig">
        <FormControlLabel
          value={FLAT}
          control={<Radio />}
          label="Flat Increase"
          sx={(theme) => ({ color: theme.palette.text.secondary })}
        />
        <FormControlLabel
          value={CHARGE_RATE_MULTIPLIER}
          control={<Radio />}
          label="Multiple of the charge rate."
          sx={(theme) => ({ color: theme.palette.text.secondary })}
        />
      </RadioGroup>

      {option === FLAT && (
        <HolidayInput
          staticProps={FLAT_PROPS}
          value={flatIncreaseValue}
          onChange={handleHolidayRateOnChange}
        />
      )}
      {option === CHARGE_RATE_MULTIPLIER && (
        <HolidayInput
          staticProps={MULTIPLIER_PROPS}
          value={chargeValue}
          onChange={handleHolidayRateOnChange}
        />
      )}
    </Box>
  );
}
